// DOCS: https://rollouter.atlassian.net/wiki/spaces/RCP/pages/8126550/Notifications

import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';
import { useUserStore } from './user';

export type Notification = {
  id: string;
} & NotificationCreateData;

export interface NotificationCreateData {
  type: NotificationType;
  title?: string;
  message: string;
  traceId?: string;
  link?: string;
}

export type NotificationType =
  | 'success'
  | 'information'
  | 'warning'
  | 'error'
  | 'question';

export const useNotifications = defineStore('notifications', () => {
  const notifications: Ref<Notification[]> = ref([]);
  const notificationTime = 6000; // 6 seconds
  const i18n = useI18n();

  const userStore = useUserStore();
  const user = computed(() => userStore.user);

  const canCreateNotification = (options: NotificationCreateData) => {
    const disableSuccessNotifications =
      user.value?.settings?.disableSuccessNotifications;

    if (disableSuccessNotifications) {
      return options.type !== 'success';
    }

    return true;
  };

  const createNotification = (options: NotificationCreateData) => {
    if (canCreateNotification(options)) {
      const id = uuid();
      notifications.value.unshift({ id, ...options });
      initAutomaticCloseTimer(id);
    }
  };

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(
      (notification) => notification.id === id
    );
    if (index === -1) return;
    notifications.value.splice(index, 1);
  };

  const createIncompleteNotification = () => {
    createNotification({
      type: 'warning',
      title: i18n.t('error-messages.incomplete-information'),
      message: i18n.t('error-messages.incomplete-information-message'),
    });
  };

  const initAutomaticCloseTimer = (id: string) => {
    setTimeout(() => {
      removeNotification(id);
    }, notificationTime);
  };

  return {
    notifications,
    createNotification,
    createIncompleteNotification,
    removeNotification,
  };
});

export const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  return String(error);
};
